"use client";

import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import type { ResumeDocument } from "@/types/cv";
import {
  createBuilderPolishPayment,
  polishBuilderResume,
} from "@/lib/cv-builder/polish-client";
import { useCvBuilderPolishStore } from "@/stores/cv-builder-polish-store";
import { useCvBuilderUpgradeStore } from "@/stores/cv-builder-upgrade-store";
import { useQueryClient } from "@tanstack/react-query";

import { useI18n } from "@/components/i18n-provider";
import { cvBuilderPolishComparisonQueryKey } from "@/lib/cv-builder/cv-builder-query-keys";
import { QrisPaymentStep } from "@/components/payments/qris-payment-step";
import { Button } from "@/components/ui/button";
import {
  ServiceUpgradeDialog,
  ServiceUpgradeIntro,
} from "@/components/upgrade/service-upgrade-dialog";
import { PolishPreviewCard } from "@/components/cv-builder/polish-preview-card";
import { PolishSuccessStep } from "@/components/cv-builder/polish-success-step";
import { useBuilderCvCheck } from "@/lib/cv-builder/cv-check-client";
import { withCvBuilderAnalyticsContext } from "@/components/cv-builder/cv-builder-analytics";
import { trackEvent } from "@/lib/analytics";
import {
  POLISH_AMOUNT,
  POLISH_PRICE_LABEL,
} from "@/lib/cv-builder/polish-pricing";

interface CvBuilderPolishDialogProps {
  resume: ResumeDocument;
  analyticsContext?: UmamiEventData;
  outputLanguage: "Indonesian" | "English";
  onPolished: (resume: ResumeDocument) => Promise<void> | void;
}

export function CvBuilderPolishDialog({
  resume,
  analyticsContext,
  outputLanguage,
  onPolished,
}: CvBuilderPolishDialogProps) {
  const { dictionary, locale } = useI18n();
  const queryClient = useQueryClient();
  const markUpgraded = useCvBuilderUpgradeStore((state) => state.markUpgraded);
  const {
    isOpen,
    phase,
    source,
    payment,
    error,
    closeDialog,
    setPreparingPayment,
    setPayment,
    setGenerating,
    setSuccess,
    setError,
    resetPayment,
  } = useCvBuilderPolishStore();
  const checkQuery = useBuilderCvCheck(resume, {
    enabled: isOpen,
    outputLanguage,
  });
  const [reviewTarget, setReviewTarget] = useState<{
    jobId: string;
    paymentId: string;
  } | null>(null);
  const autoStartedRef = useRef(false);

  const startPayment = async () => {
    setReviewTarget(null);
    setPreparingPayment();

    try {
      const nextPayment = await createBuilderPolishPayment();
      setPayment(nextPayment);
      trackEvent(
        "builder_ai_polish_payment_created",
        withCvBuilderAnalyticsContext(analyticsContext, {
          source: "builder_polish_dialog",
          opened_from: source,
          amount: nextPayment.amount,
          // Explicit so pre- and post-price-change cohorts stay separable.
          price_idr: POLISH_AMOUNT,
          currency: "IDR",
        }),
      );
    } catch (err) {
      console.error("Failed to create builder polish payment", err);
      resetPayment();
      setError("Gagal menyiapkan pembayaran. Coba lagi sebentar lagi.");
    }
  };

  // Only the upgrade modal has already shown a price and pitched the product,
  // so only that source may skip the explainer and open straight into payment.
  // From the toolbar the user has seen neither, and jumping to a QR code there
  // is what produced a 91% payment-created rate against a 7.5% completion rate.
  // Runs once per open; on error the user retries manually (never auto-loops).
  useEffect(() => {
    if (!isOpen) {
      autoStartedRef.current = false;
      return;
    }
    if (autoStartedRef.current || phase !== "intro") return;
    if (source !== "upgrade_modal") return;
    autoStartedRef.current = true;
    void startPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, phase, source]);

  // Impression for the new consideration step, so the intro can be measured
  // against the payment it now precedes.
  useEffect(() => {
    if (!isOpen || phase !== "intro" || source !== "toolbar") return;
    trackEvent(
      "builder_polish_intro_shown",
      withCvBuilderAnalyticsContext(analyticsContext, {
        source: "builder_polish_dialog",
        price_idr: POLISH_AMOUNT,
      }),
    );
  }, [analyticsContext, isOpen, phase, source]);

  const handleIntroPay = () => {
    trackEvent(
      "builder_polish_intro_pay_click",
      withCvBuilderAnalyticsContext(analyticsContext, {
        source: "builder_polish_dialog",
        price_idr: POLISH_AMOUNT,
      }),
    );
    void startPayment();
  };

  const handlePaymentSuccess = async (paymentId: string) => {
    setGenerating();

    // payment_success (product: cv_enhancement_ai) is recorded server-side
    // once the payment is authoritatively confirmed, not from this callback.
    markUpgraded();

    try {
      const rewrite = await polishBuilderResume({
        resume,
        outputLanguage,
        paymentId,
      });
      await onPolished(rewrite.rewritten_cv);
      // The job row now holds a completed before/after, so the compare entry
      // point should appear without waiting for a reload.
      await queryClient.invalidateQueries({
        queryKey: cvBuilderPolishComparisonQueryKey,
      });
      setReviewTarget({ jobId: rewrite.jobId, paymentId });
      setSuccess();
      trackEvent(
        "builder_ai_polish_completed",
        withCvBuilderAnalyticsContext(analyticsContext, {
          source: "builder_polish_dialog",
        }),
      );
    } catch (err) {
      console.error("Failed to polish builder resume", err);
      resetPayment();
      setError(
        "Gagal merapikan CV. Coba lagi sebentar lagi atau lanjut edit manual.",
      );
    }
  };


  return (
    <ServiceUpgradeDialog
      open={isOpen}
      onOpenChange={(open) => !open && closeDialog()}
      title="Biar CV kamu lebih siap dibaca recruiter"
      description={
        <>
          Sekali bayar {POLISH_PRICE_LABEL}. AI Hyred merapikan ringkasan dan
          bullet pengalaman dari data yang sudah kamu isi, lalu hasilnya
          langsung masuk ke preview.
        </>
      }
    >
      {phase === "intro" && source === "toolbar" ? (
        <>
          {checkQuery.data ? (
            <PolishPreviewCard sample={checkQuery.data.sample_rewrite} />
          ) : checkQuery.isLoading ? (
            <div
              className="rounded-2xl border border-border bg-card p-4"
              role="status"
              aria-live="polite"
            >
              <p className="text-sm font-semibold text-foreground">
                Menyiapkan contoh hasil dari CV kamu...
              </p>
              <div className="mt-3 grid gap-2">
                <div className="h-3 w-4/5 rounded bg-muted motion-safe:animate-pulse" />
                <div className="h-3 w-3/5 rounded bg-muted motion-safe:animate-pulse" />
              </div>
            </div>
          ) : null}

          <ServiceUpgradeIntro
            label="AI Polish"
            priceLabel={POLISH_PRICE_LABEL}
            priceCaption="Sekali bayar"
            audienceNote="AI merapikan ringkasan dan bullet pengalaman supaya recruiter lebih cepat melihat alasan kenapa kamu layak lanjut dibaca."
            benefits={[
              "Ringkasan dan semua bullet pengalaman ditulis ulang lebih tajam.",
              "Data asli kamu dipertahankan. AI hanya merapikan kata, struktur, dan kata kunci.",
              "Hasilnya langsung masuk ke preview dan siap kamu download.",
            ]}
            error={error}
            secondaryAction={
              <Button type="button" variant="outline" onClick={closeDialog}>
                Nanti saja
              </Button>
            }
            primaryAction={
              <Button type="button" onClick={handleIntroPay}>
                Bayar {POLISH_PRICE_LABEL}
              </Button>
            }
          />
        </>
      ) : null}

      {(phase === "intro" && source === "upgrade_modal") ||
      phase === "preparing_payment" ? (
        error ? (
          <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
            <p className="text-sm text-destructive">{error}</p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" onClick={closeDialog}>
                Nanti saja
              </Button>
              <Button type="button" onClick={startPayment}>
                Coba lagi
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center gap-3 py-10 text-center"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div>
              <p className="font-semibold text-foreground">
                Menyiapkan pembayaran...
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                QRIS {POLISH_PRICE_LABEL} akan muncul setelah checkout siap.
              </p>
            </div>
          </div>
        )
      ) : null}

      {phase === "payment" && payment ? (
        <QrisPaymentStep
          paymentId={payment.paymentId}
          qrString={payment.qrString}
          amount={payment.amount}
          labels={dictionary.payment.qris}
          onSuccess={handlePaymentSuccess}
          onRetry={resetPayment}
        />
      ) : null}

      {phase === "generating" ? (
        <div
          className="flex flex-col items-center justify-center gap-3 py-10 text-center"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div>
            <p className="font-semibold text-foreground">
              Menyiapkan versi CV yang lebih polished...
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Jangan tutup halaman ini sampai proses selesai.
            </p>
          </div>
        </div>
      ) : null}

      {phase === "success" ? (
        <PolishSuccessStep
          reviewTarget={reviewTarget}
          analyticsContext={analyticsContext}
          compareHref={`/${locale}/dashboard/cv-builder/compare`}
          compareLabel={dictionary.cvBuilder.polishCompare.viewChanges}
          onClose={closeDialog}
        />
      ) : null}
    </ServiceUpgradeDialog>
  );
}
